import { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Tooltip } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { SlidersHorizontal, X } from "lucide-react";

import { Button } from "../Button";
import { CustomSelector } from "../CustomSelector";
import { CustomTextField } from "../CustomTextField";
import { FilterSwitchGroup } from "../FilterSwitchGroup";
import { SortSelectorPopover } from "../SortSelectorPopover";
import { useTheme as useCustomTheme } from "../../theme/useTheme";

const ICON_BUTTON_SX = (neutrals) => ({
  borderColor: neutrals.gray[200],
  color: neutrals.gray[600],
  flexShrink: 0,
  minWidth: "32px",
  width: "32px",
  height: "32px",
  padding: "4px",
  boxShadow: "none",
});

// Estilo del ViewModeSelector cuando se coloca en el toolbar (misma altura que el resto de botones).
export const toolbarViewModeButtonSx = ({ theme }) => ({
  border: `1px solid ${theme.palette.border?.input || theme.palette.divider}`,
  borderColor: theme.palette.border?.input || theme.palette.divider,
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  width: { xs: "fit-content", sm: "112px" },
  minWidth: { xs: "44px", sm: "112px" },
  height: "32px",
  padding: "6px 10px",
  flexShrink: 0,
  borderRadius: "8px",
  boxShadow: "none",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.border?.default || theme.palette.divider,
    backgroundColor: theme.palette.background.default,
  },
  "&:focus, &:focus-visible, &:active, &.Mui-focusVisible": {
    outline: "none",
    borderColor: theme.palette.border?.input || theme.palette.divider,
    boxShadow: "none",
  },
});

const TabLabel = ({ label, count, isActive }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
    {label}
    {count !== undefined && count !== null ? (
      <Box
        component="span"
        sx={{ fontWeight: isActive ? 600 : 500, opacity: isActive ? 1 : 0.8 }}
      >
        {count}
      </Box>
    ) : null}
  </Box>
);

TabLabel.propTypes = {
  label: PropTypes.node,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isActive: PropTypes.bool,
};

const ToolbarChip = ({ label, onRemove }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        height: "28px",
        pl: 1.25,
        pr: onRemove ? 0.5 : 1.25,
        borderRadius: "6px",
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.primary.main,
        fontSize: "0.75rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {label}
      {onRemove ? (
        <IconButton
          size="small"
          aria-label="Quitar filtro"
          onClick={onRemove}
          sx={{ width: 20, height: 20, p: 0, color: "inherit" }}
        >
          <X size={12} />
        </IconButton>
      ) : null}
    </Box>
  );
};

ToolbarChip.propTypes = {
  label: PropTypes.node.isRequired,
  onRemove: PropTypes.func,
};

// Tabs que no caben en su hueco: se pintan como selector. Se mide una copia oculta de los tabs.
function useTabsOverflow(enabled) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || typeof ResizeObserver === "undefined") return undefined;

    const check = () => setIsOverflowing(measure.scrollWidth > container.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(container);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [enabled]);

  return { containerRef, measureRef, isOverflowing: enabled && isOverflowing };
}

const tabOptionText = (tab) =>
  tab.count !== undefined && tab.count !== null ? `${tab.label} · ${tab.count}` : tab.label;

/**
 * Toolbar integrado en la cabecera de DataTable: buscador siempre visible a la
 * izquierda, después los tabs (estilo AveronCRMERP) y chips, y a la derecha las
 * acciones (slot libre, ordenar, filtrar).
 */
export function DataTableToolbar({
  tabs,
  activeTab,
  onTabChange,
  tabsDisabled = false,
  showSearch = true,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Buscar...",
  sortFieldOptions,
  sortField,
  onSortFieldChange,
  sortDirection = "desc",
  onSortDirectionChange,
  sortDirectionOptions = [],
  showSortDirection = true,
  onFilterClick,
  activeFiltersCount = 0,
  filterTooltip = "Filtrar",
  filterChips,
  toolbarStart,
  toolbarEnd,
  variant = "embedded",
  sx,
  ...rootProps
}) {
  const { neutrals, theme } = useCustomTheme();
  const hasTabs = Array.isArray(tabs) && tabs.length > 0;
  const hasSearch = Boolean(showSearch && onSearchChange);
  const hasSort = Array.isArray(sortFieldOptions) && sortFieldOptions.length > 0 && onSortFieldChange;


  const tabOptions = hasTabs
    ? tabs.map((tab) => ({
        value: tab.value,
        label: (
          <TabLabel label={tab.label} count={tab.count} isActive={tab.value === activeTab} />
        ),
      }))
    : [];

  const { containerRef, measureRef, isOverflowing } = useTabsOverflow(hasTabs);

  const tabsSwitch = (
    <FilterSwitchGroup
      options={tabOptions}
      selectedValue={activeTab}
      onChange={onTabChange}
      disabled={tabsDisabled}
      containerSx={{ flexWrap: "nowrap", flexShrink: 0 }}
      optionsSx={{ flexWrap: "nowrap" }}
    />
  );

  const chips = filterChips?.map((chip) => (
    <ToolbarChip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
  ));

  return (
    <Box
      {...rootProps}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        flexShrink: 0,
        ...(variant === "standalone"
          ? {
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.paper,
            }
          : { borderBottom: `1px solid ${theme.palette.divider}` }),
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1.25, sm: 1.5 },
          minWidth: 0,
        }}
      >
        {toolbarStart ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            {toolbarStart}
          </Box>
        ) : null}

        {hasSearch ? (
          <Box sx={{ width: { xs: "100%", sm: "250px" }, flexShrink: 0 }}>
            <CustomTextField
              placeholder={searchPlaceholder}
              value={searchValue ?? ""}
              onChange={(event) => onSearchChange(event.target.value)}
              isSearch
            />
          </Box>
        ) : null}

        {hasTabs || filterChips?.length ? (
          <Box
            ref={containerRef}
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 0,
              flexWrap: "nowrap",
              overflow: "hidden",
            }}
          >
            {hasTabs ? (
              <Box
                ref={measureRef}
                aria-hidden
                sx={{
                  position: "absolute",
                  visibility: "hidden",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "max-content",
                }}
              >
                {tabsSwitch}
                {chips}
              </Box>
            ) : null}

            {hasTabs && isOverflowing ? (
              <Box sx={{ width: 200, flexShrink: 0 }}>
                <CustomSelector
                  size="sm"
                  value={activeTab}
                  onChange={onTabChange}
                  options={tabs.map((tab) => ({ value: tab.value, label: tabOptionText(tab) }))}
                  disabled={tabsDisabled}
                />
              </Box>
            ) : hasTabs ? (
              tabsSwitch
            ) : null}

            {chips}
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            flexShrink: 0,
            flexWrap: "wrap",
            ml: { sm: "auto" },
          }}
        >
          {toolbarEnd}

          {hasSort ? (
            <SortSelectorPopover
              sortDirection={sortDirection}
              sortField={sortField}
              onSortDirectionChange={onSortDirectionChange}
              onSortFieldChange={onSortFieldChange}
              directionOptions={sortDirectionOptions}
              fieldOptions={sortFieldOptions}
              showDirection={showSortDirection && Boolean(onSortDirectionChange)}
            />
          ) : null}

          {onFilterClick ? (
            <Tooltip title={filterTooltip} arrow>
              <Button
                variant="outline"
                size="sm"
                onClick={onFilterClick}
                aria-label={filterTooltip}
                sx={{ ...ICON_BUTTON_SX(neutrals), position: "relative", overflow: "visible" }}
              >
                <SlidersHorizontal size={16} />
                {activeFiltersCount > 0 ? (
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      minWidth: 16,
                      height: 16,
                      px: 0.5,
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontSize: "0.625rem",
                      fontWeight: 600,
                      lineHeight: 1,
                    }}
                  >
                    {activeFiltersCount}
                  </Box>
                ) : null}
              </Button>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

DataTableToolbar.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onTabChange: PropTypes.func,
  tabsDisabled: PropTypes.bool,
  showSearch: PropTypes.bool,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  sortFieldOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
  ),
  sortField: PropTypes.string,
  onSortFieldChange: PropTypes.func,
  sortDirection: PropTypes.oneOf(["asc", "desc"]),
  onSortDirectionChange: PropTypes.func,
  sortDirectionOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
  ),
  showSortDirection: PropTypes.bool,
  onFilterClick: PropTypes.func,
  activeFiltersCount: PropTypes.number,
  filterTooltip: PropTypes.string,
  filterChips: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      onRemove: PropTypes.func,
    })
  ),
  toolbarStart: PropTypes.node,
  toolbarEnd: PropTypes.node,
  // "embedded": cabecera dentro de DataTable. "standalone": card propia (vistas sin tabla).
  variant: PropTypes.oneOf(["embedded", "standalone"]),
  sx: PropTypes.object,
};

export default DataTableToolbar;
